import { defineEvent } from "@visioni/a3s-analytics";
import type { BiometryType } from "@src/hooks/useBiometricAuth";

/** The detected biometry, or `"unknown"` before detection or on a device without it. */
type BiometryProps = { biometry_type: BiometryType | "unknown" };

// Auth0 login: the sign-in screen (`AuthProvider`), on web and native.

export const Logout = defineEvent("Logout");
export const Auth0LoginStarted = defineEvent("Auth0 Login Started");
/** An Auth0 token exchange set the session. The user is identified (id only) just before, so the event carries no id or email. */
export const Auth0LoginCompleted = defineEvent("Auth0 Login Completed");
/** OAuth 2.0 / OpenID Connect authorization error codes, as returned in the redirect's `error` param. */
export const OAUTH_ERROR_CODES = [
  "invalid_request",
  "unauthorized_client",
  "access_denied",
  "unsupported_response_type",
  "invalid_scope",
  "server_error",
  "temporarily_unavailable",
  "interaction_required",
  "login_required",
  "account_selection_required",
  "consent_required",
] as const;

/**
 * Why an Auth0 login failed. Never free text: Auth0's `error_description` and
 * error messages can carry user input, so only these codes are sent.
 * - An `OAUTH_ERROR_CODES` value, or `oauth_other`: Auth0 returned an error to the prompt.
 * - `prompt_failed`: the browser prompt itself threw.
 * - `csrf_mismatch`: the returned `state` was unreadable or its CSRF token did not match.
 * - `missing_code_verifier`: no PKCE code verifier to exchange the code with.
 * - `config_missing`: the Auth0 config had not loaded.
 * - `exchange_failed`: the token request for the code failed.
 * - `session_failed`: the tokens could not be stored as the session.
 * - `unknown`: anything else.
 */
export type Auth0LoginErrorType =
  | (typeof OAUTH_ERROR_CODES)[number]
  | "oauth_other"
  | "prompt_failed"
  | "csrf_mismatch"
  | "missing_code_verifier"
  | "config_missing"
  | "exchange_failed"
  | "session_failed"
  | "unknown";

export const Auth0LoginFailed = defineEvent<{ error_type: Auth0LoginErrorType }>("Auth0 Login Failed");
/** The user closed the sign-in sheet before finishing. */
export const Auth0LoginCancelled = defineEvent("Auth0 Login Cancelled");

// Shown on the auth screens (`ContactUsButton`), on web and native.

export const ContactUsClicked = defineEvent("Contact Us Clicked");

// Biometric and PIN onboarding and unlock: native only.

export const BiometricSetupStarted = defineEvent<BiometryProps & { is_available: boolean }>("Biometric Setup Started");
export const BiometricSetupEnabled = defineEvent<BiometryProps>("Biometric Setup Enabled");
export const BiometricSetupSkipped = defineEvent<BiometryProps>("Biometric Setup Skipped");
export const BiometricVerifyStarted = defineEvent<BiometryProps>("Biometric Verify Started");
export const BiometricVerifySuccess = defineEvent<BiometryProps>("Biometric Verify Success");
export const BiometricVerifyFailed = defineEvent<BiometryProps>("Biometric Verify Failed");

export const PinSetupStarted = defineEvent("PIN Setup Started");
export const PinSetupCompleted = defineEvent("PIN Setup Completed");
export const PinSetupFailed = defineEvent<{ reason: "pin_mismatch" | "max_attempts_reached"; attempts: number }>(
  "PIN Setup Failed",
);
/** The PIN screens' `flow` route param: `change` sends PIN Change events instead of PIN Setup. */
export type PinFlow = "setup" | "change";

export function pinFlowFromParam(param: unknown): PinFlow {
  return param === "change" ? "change" : "setup";
}

export const PinChangeStarted = defineEvent("PIN Change Started");
export const PinChangeCompleted = defineEvent("PIN Change Completed");
export const PinChangeFailed = defineEvent<{
  step: "current_pin" | "new_pin";
  reason: "wrong_pin" | "pin_mismatch" | "max_attempts_reached";
  attempts: number;
}>("PIN Change Failed");

export const PinVerifySuccess = defineEvent("PIN Verify Success");
/** `attempts` is absent for `storage_error`, where the stored PIN could not be read. */
export const PinVerifyFailed = defineEvent<{
  reason: "wrong_pin" | "max_attempts_reached" | "storage_error";
  attempts?: number;
}>("PIN Verify Failed");
